import {
  BarChart3,
  Coins,
  MessageSquare,
  RefreshCw,
  Send,
  Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ObservabilityFilters } from "@/api/types";
import { tokenStorage } from "@/auth/tokenStorage";
import {
  BarChartVertical,
  BarList,
  ChartCard,
  DonutChart,
} from "@/components/charts";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionCard } from "@/components/ui/section-card";
import { StatCard } from "@/components/ui/stat-card";
import { DisabledBanner } from "@/features/observability/components/DisabledBanner";
import {
  useCostByDay,
  useCostByModel,
  useCostByTeam,
  useObservabilityStatus,
  useSummary,
  useTopAgents,
} from "@/features/observability/hooks/useObservability";
import { ObservabilityAgentPanel } from "@/features/observability-agent/components/ObservabilityAgentPanel";
import { formatCostUsd } from "@/lib/cost";
import { env } from "@/lib/env";

function defaultFilters(): ObservabilityFilters {
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    from: from.toISOString(),
    to: to.toISOString(),
  };
}

/**
 * Dashboard admin. Renderiza apos confirmar status enabled=true.
 */
export function ObservabilityHomePage() {
  const { t } = useTranslation("observability");
  const status = useObservabilityStatus();
  const [filters, setFilters] = useState<ObservabilityFilters>(() =>
    defaultFilters(),
  );

  const summary = useSummary(filters);
  const costByDay = useCostByDay(filters);
  const costByModel = useCostByModel(filters);
  const costByTeam = useCostByTeam(filters);
  const topAgents = useTopAgents({ ...filters, limit: 10 });

  const queries = useMemo(
    () => [summary, costByDay, costByModel, costByTeam, topAgents],
    [summary, costByDay, costByModel, costByTeam, topAgents],
  );

  const costByDayData = useMemo(
    () =>
      (costByDay.data ?? []).map((d) => ({
        label: d.date.slice(5),
        value: d.cost,
      })),
    [costByDay.data],
  );
  const costByTeamData = useMemo(
    () =>
      (costByTeam.data ?? []).map((d) => ({
        label: d.team_name,
        value: d.cost,
      })),
    [costByTeam.data],
  );
  const costByModelData = useMemo(
    () =>
      (costByModel.data ?? []).map((d) => ({
        label: d.model,
        value: d.cost,
      })),
    [costByModel.data],
  );

  if (status.isLoading) return <LoadingState />;
  if (status.isError)
    return (
      <ErrorState error={status.error} onRetry={() => void status.refetch()} />
    );
  if (status.data && !status.data.enabled) {
    return <DisabledBanner reason={status.data.reason} />;
  }

  async function exportData(format: "csv" | "json") {
    const token = await tokenStorage.getToken();
    const params = new URLSearchParams();
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    const url = `${env.API_BASE_URL}/observability/cost-by-day?${params.toString()}&format=${format}`;
    const resp = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!resp.ok) return;
    const blob = await resp.blob();
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `observability_cost-by-day.${format}`;
    link.click();
    window.URL.revokeObjectURL(link.href);
  }

  function refreshAll() {
    for (const q of queries) void q.refetch();
  }

  return (
    <div className="flex items-start gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-6">
        <PageHeader
          title={
            <span className="flex items-center gap-2">
              <BarChart3 className="text-brand-500 h-6 w-6" /> {t("title")}
            </span>
          }
          description={t("subtitle")}
          actions={
            <>
              <Button variant="outline" size="sm" onClick={refreshAll}>
                <RefreshCw className="h-3 w-3" />
                {t("actions.refresh")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportData("csv")}
              >
                {t("actions.exportCsv")}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportData("json")}
              >
                {t("actions.exportJson")}
              </Button>
            </>
          }
        />

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="obs-from">{t("filters.from")}</Label>
            <Input
              id="obs-from"
              type="datetime-local"
              value={filters.from ? filters.from.slice(0, 16) : ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  from: e.target.value
                    ? new Date(e.target.value).toISOString()
                    : undefined,
                }))
              }
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="obs-to">{t("filters.to")}</Label>
            <Input
              id="obs-to"
              type="datetime-local"
              value={filters.to ? filters.to.slice(0, 16) : ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  to: e.target.value
                    ? new Date(e.target.value).toISOString()
                    : undefined,
                }))
              }
            />
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label={t("kpi.totalCost")}
            value={summary.data ? formatCostUsd(summary.data.total_cost) : "—"}
            icon={Coins}
            loading={summary.isLoading}
          />
          <StatCard
            label={t("kpi.totalRequests")}
            value={
              summary.data
                ? summary.data.total_requests.toLocaleString("pt-BR")
                : "—"
            }
            icon={Send}
            loading={summary.isLoading}
          />
          <StatCard
            label={t("kpi.totalTokens")}
            value={
              summary.data
                ? summary.data.total_tokens.toLocaleString("pt-BR")
                : "—"
            }
            icon={Sparkles}
            loading={summary.isLoading}
          />
          <StatCard
            label={t("kpi.activeThreads")}
            value={
              summary.data
                ? summary.data.active_threads.toLocaleString("pt-BR")
                : "—"
            }
            icon={MessageSquare}
            loading={summary.isLoading}
          />
        </div>

        {/* Cost by day */}
        <ChartCard
          title={t("charts.costByDay")}
          height={280}
          loading={costByDay.isLoading}
          isEmpty={!costByDay.isLoading && costByDayData.length === 0}
          emptyMessage={t("empty.noDataInPeriod")}
        >
          <BarChartVertical data={costByDayData} valueFormatter={formatCostUsd} />
        </ChartCard>

        <div className="grid gap-3 lg:grid-cols-2">
          <SectionCard title={t("charts.costByTeam")}>
            {costByTeam.isLoading ? (
              <div className="bg-ink-100/40 h-32 animate-pulse rounded-xl" />
            ) : costByTeamData.length === 0 ? (
              <p className="text-ink-500 py-4 text-center text-sm">
                {t("empty.dash")}
              </p>
            ) : (
              <BarList data={costByTeamData} valueFormatter={formatCostUsd} />
            )}
          </SectionCard>

          <ChartCard
            title={t("charts.costByModel")}
            height={280}
            loading={costByModel.isLoading}
            isEmpty={!costByModel.isLoading && costByModelData.length === 0}
            emptyMessage={t("empty.dash")}
          >
            <DonutChart
              data={costByModelData}
              valueFormatter={formatCostUsd}
              centerLabel={t("kpi.totalCost")}
              centerValue={
                summary.data ? formatCostUsd(summary.data.total_cost) : undefined
              }
            />
          </ChartCard>
        </div>

        <SectionCard title={t("charts.topAgents")}>
          {topAgents.data && topAgents.data.length === 0 ? (
            <p className="text-ink-500 text-sm">{t("empty.dash")}</p>
          ) : null}
          {topAgents.data && topAgents.data.length > 0 ? (
            <table className="w-full text-xs">
              <thead className="text-ink-500 text-left text-[10px] tracking-wide uppercase">
                <tr>
                  <th>{t("columns.agent")}</th>
                  <th>{t("columns.cost")}</th>
                  <th>{t("columns.requests")}</th>
                  <th>{t("columns.tokens")}</th>
                </tr>
              </thead>
              <tbody>
                {topAgents.data.map((r) => (
                  <tr key={r.agent_id} className="border-t">
                    <td className="py-1">
                      {r.agent_name}
                      {r.is_external ? (
                        <span className="text-ink-500 ml-1.5 rounded border px-1 py-0.5 text-[10px] uppercase">
                          {t("labels.external")}
                        </span>
                      ) : null}
                    </td>
                    <td className="tabular py-1">{formatCostUsd(r.cost)}</td>
                    <td className="tabular py-1">
                      {r.requests.toLocaleString("pt-BR")}
                    </td>
                    <td className="tabular py-1">
                      {r.tokens.toLocaleString("pt-BR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </SectionCard>
      </div>
      <ObservabilityAgentPanel
        dashboard={{ date_from: filters.from, date_to: filters.to }}
      />
    </div>
  );
}
